const BASE_URL = process.env.REACT_APP_COMPLIANCE_API_URL || 'https://apiv2.applyzer.de/compliance';
const APP_API_URL = process.env.REACT_APP_API_URL || "https://apiv2.applyzer.de";

class ApiService {
  constructor() {
    this.baseUrl = BASE_URL;
    this.apiurl = APP_API_URL;
  }

  getAuthHeaders(token) {
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Cache-Control': 'no-cache',
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Headers": "Origin, X-Requested-With, Content-Type, Accept"
    };
  }

  getFetchOptions(headers = {}) {
    return {
      mode: 'cors',
      credentials: 'omit',
      headers: headers
    };
  }

  async uploadFile(file, token, analysisType = 'default', projectId = null, sessionId = null, scorecardNumbers = null) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('analysis_type', analysisType);
    
    if (projectId) {
      formData.append('project_id', projectId);
    }
    
    if (sessionId) {
      formData.append('session_id', sessionId);
    }else{
      formData.append('session_id', 'new');
    }

    if (scorecardNumbers) {
      formData.append('scorecard_numbers', scorecardNumbers);
    }

    const response = await fetch(`${this.baseUrl}/upload`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: {
        'Authorization': `Bearer ${token}`,

        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Headers": "Origin, X-Requested-With, Content-Type, Accept",

      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Upload failed');
    }

    return response.json();
  }

  async getTaskStatus(taskId, token) {
    const response = await fetch(`${this.baseUrl}/task-status/${taskId}`, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get task status');
    }

    return response.json();
  }

  async getUserFiles(token) {
    const response = await fetch(`${this.baseUrl}/files`, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get files');
    }

    return response.json();
  }

  async sendChatMessage(message, token, sessionId = null, projectId = null) {
    const payload = {
      message,
      session_id: sessionId
    };
    
    if (projectId) {
      payload.project_id = projectId;
    }

    const response = await fetch(`${this.baseUrl}/chat`, {
      method: 'POST',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send message');
    }

    return response.json();
  }

  async getChatHistory(token, limit = 50, sessionId = null, projectId = null, includeArchived = false) {
    let url = `${this.baseUrl}/chat/history?limit=${limit}`;
    if (sessionId) {
      url += `&session_id=${sessionId}`;
    }
    if (projectId) {
      url += `&project_id=${projectId}`;
    }
    if (includeArchived) {
      url += `&include_archived=true`;
    }

    const response = await fetch(url, {
      headers: this.getAuthHeaders(token)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get chat history');
    }

    return response.json();
  }

  async getChatSessions(token, projectId = null) {
    let url = `${this.baseUrl}/chat/sessions`;
    if (projectId) {
      url += `?project_id=${projectId}`;
    }

    const response = await fetch(url, {
      headers: this.getAuthHeaders(token)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get chat sessions');
    }

    return response.json();
  }

  async deleteChatMessage(chatId, token, projectId = null) {
    let url = `${this.baseUrl}/chat/${chatId}`;
    if (projectId) {
      url += `?project_id=${projectId}`;
    }

    const response = await fetch(url, {
      method: 'DELETE',
      headers: this.getAuthHeaders(token)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete message');
    }

    return response.json();
  }

  async login(email, password) {
    const response = await fetch(`${this.apiurl}/login`, {
      method: "POST",
      ...this.getFetchOptions({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || error.message || 'Login failed');
    }

    return response.json();
  }

  async checkHealth() {
    const response = await fetch(`${this.baseUrl}/health`);
    
    if (!response.ok) {
      throw new Error('Health check failed');
    }

    return response.json();
  }

  async checkComplianceAccess(token) {
    const response = await fetch(`${this.baseUrl}/check-access`, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      throw new Error('Failed to check compliance access');
    }

    return response.json();
  }

  async submitFeedback(email, body) {
    const response = await fetch(`${this.apiurl}/general/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
        body: body
      })
    });

    if (!response.ok) {
      throw new Error('Failed to submit feedback');
    }

    return response.json();
  }

  async getComplianceUsage(token) {
    const response = await fetch(`${this.baseUrl}/usage`, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      throw new Error('Failed to get compliance usage');
    }

    return response.json();
  }

  // Project Management Methods
  async getProjects(token) {
    const response = await fetch(`${this.baseUrl}/projects`, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get projects');
    }

    return response.json();
  }

  async createProject(projectData, token) {
    const response = await fetch(`${this.baseUrl}/projects`, {
      method: 'POST',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify(projectData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create project');
    }

    return response.json();
  }

  async getProject(projectId, token) {
    const response = await fetch(`${this.baseUrl}/projects/${projectId}`, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get project');
    }

    return response.json();
  }

  async updateProject(projectId, projectData, token) {
    const response = await fetch(`${this.baseUrl}/projects/${projectId}`, {
      method: 'PUT',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify(projectData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update project');
    }

    return response.json();
  }

  async deleteProject(projectId, token) {
    const response = await fetch(`${this.baseUrl}/projects/${projectId}`, {
      method: 'DELETE',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete project');
    }

    return response.json();
  }

  // Project Permissions Methods
  async inviteUserToProject(projectId, email, permissionLevel, token) {
    const response = await fetch(`${this.baseUrl}/projects/${projectId}/permissions`, {
      method: 'POST',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify({
        email,
        permission_level: permissionLevel
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to invite user');
    }

    return response.json();
  }

  async getProjectPermissions(projectId, token) {
    const response = await fetch(`${this.baseUrl}/projects/${projectId}/permissions`, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get project permissions');
    }

    return response.json();
  }

  async updateProjectPermission(projectId, userId, permissionLevel, token) {
    const response = await fetch(`${this.baseUrl}/projects/${projectId}/permissions/${userId}`, {
      method: 'PUT',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify({
        permission_level: permissionLevel
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update permission');
    }

    return response.json();
  }

  async removeProjectPermission(projectId, userId, token) {
    const response = await fetch(`${this.baseUrl}/projects/${projectId}/permissions/${userId}`, {
      method: 'DELETE',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to remove permission');
    }

    return response.json();
  }

  async checkProjectPermission(projectId, token) {
    const response = await fetch(`${this.baseUrl}/projects/${projectId}/check-permission`, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to check permission');
    }

    return response.json();
  }

  // Threshold Settings Methods
  async getThresholdSettings(token) {
    const response = await fetch(`${this.baseUrl}/threshold-settings`, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get threshold settings');
    }

    return response.json();
  }

  async updateThresholdSettings(thresholdData, token) {
    const response = await fetch(`${this.baseUrl}/threshold-settings`, {
      method: 'POST',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify(thresholdData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update threshold settings');
    }

    return response.json();
  }

  async getProjectThresholds(projectId, token) {
    const response = await fetch(`${this.baseUrl}/projects/${projectId}/threshold-settings`, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get project thresholds');
    }

    return response.json();
  }

  async updateProjectThresholds(projectId, thresholdData, token) {
    const response = await fetch(`${this.baseUrl}/projects/${projectId}/threshold-settings`, {
      method: 'POST',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify(thresholdData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update project thresholds');
    }

    return response.json();
  }

  async getThresholdDefaults(token) {
    const response = await fetch(`${this.baseUrl}/threshold-defaults`, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get threshold defaults');
    }

    return response.json();
  }

  async deleteProjectThresholds(projectId, token) {
    const response = await fetch(`${this.baseUrl}/projects/${projectId}/threshold-settings`, {
      method: 'DELETE',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete project thresholds');
    }

    return response.json();
  }

  // User Instructions Methods
  async saveUserInstructions(instructionData, token) {
    const response = await fetch(`${this.baseUrl}/instructions`, {
      method: 'POST',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify(instructionData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to save instructions');
    }

    return response.json();
  }

  async getUserInstructions(token, projectId = null, type = 'chat') {
    let url = `${this.baseUrl}/instructions`;
    const params = new URLSearchParams();
    
    if (type) {
      params.append('type', type);
    }
    if (projectId) {
      params.append('project_id', projectId);
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get instructions');
    }

    return response.json();
  }

  async getUserInstructionsList(token, type = null) {
    let url = `${this.baseUrl}/instructions/list`;
    if (type) {
      url += `?type=${type}`;
    }

    const response = await fetch(url, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get instructions list');
    }

    return response.json();
  }

  async getAllUserInstructionsByType(token, type) {
    let url = `${this.baseUrl}/instructions?all=true`;
    if (type) {
      url += `&type=${type}`;
    }

    const response = await fetch(url, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get instructions');
    }

    return response.json();
  }

  async deleteUserInstructions(instructionId, token) {
    const response = await fetch(`${this.baseUrl}/instructions/${instructionId}`, {
      method: 'DELETE',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete instructions');
    }

    return response.json();
  }

  // Document Management Methods
  async uploadDocument(file, token, projectId = null, documentType = null) {
    const formData = new FormData();
    formData.append('file', file);
    if (projectId) {
      formData.append('project_id', projectId);
    }
    if (documentType) {
      formData.append('document_type', documentType);
    }

    const response = await fetch(`${this.baseUrl}/upload-document`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: {
        'Authorization': `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Headers": "Origin, X-Requested-With, Content-Type, Accept",
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Document upload failed');
    }

    return response.json();
  }

  async getDocuments(token, projectId = null, documentType = null) {
    let url = `${this.baseUrl}/documents`;
    const params = new URLSearchParams();
    
    if (projectId) {
      params.append('project_id', projectId);
    }
    if (documentType) {
      params.append('document_type', documentType);
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get documents');
    }

    return response.json();
  }

  async getDocument(documentId, token) {
    const response = await fetch(`${this.baseUrl}/documents/${documentId}`, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get document');
    }

    return response.json();
  }

  async deleteDocument(documentId, token) {
    const response = await fetch(`${this.baseUrl}/documents/${documentId}`, {
      method: 'DELETE',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete document');
    }

    return response.json();
  }

  async exportChatSession(sessionId, token, projectId = null) {
    let url = `${this.baseUrl}/export-session/${sessionId}`;
    if (projectId) {
      url += `?project_id=${projectId}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      headers: {
        'Authorization': `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Headers": "Origin, X-Requested-With, Content-Type, Accept",
      }
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to export chat session');
    }

    return response;
  }

  async getProjectDocuments(projectId, token) {
    const response = await fetch(`${this.baseUrl}/projects/${projectId}/documents`, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get project documents');
    }

    return response.json();
  }

  async getSessionSummary(sessionId, token, projectId = null) {
    let url = `${this.baseUrl}/session-summary/${sessionId}`;
    if (projectId) {
      url += `?project_id=${projectId}`;
    }

    const response = await fetch(url, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get session summary');
    }

    return response.json();
  }

  async getNewSessionSummary(sessionId, token, projectId = null) {
    let url = `${this.baseUrl}/test-new-session-summary/${sessionId}`;
    if (projectId) {
      url += `?project_id=${projectId}`;
    }

    const response = await fetch(url, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get new session summary');
    }

    return response.json();
  }

  async copyUserThresholdsToProject(projectId, token) {
    const response = await fetch(`${this.baseUrl}/projects/${projectId}/threshold-settings/copy-from-user`, {
      method: 'POST',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to copy user thresholds to project');
    }

    return response.json();
  }

  // Session naming methods
  async setSessionName(sessionId, name, token, projectId = null) {
    const payload = {
      name: name.trim()
    };
    
    if (projectId) {
      payload.project_id = projectId;
    }

    const response = await fetch(`${this.baseUrl}/chat/sessions/${sessionId}/name`, {
      method: 'PUT',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update session name');
    }

    return response.json();
  }

  async getSessions(token, projectId = null) {
    let url = `${this.baseUrl}/chat/sessions`;
    if (projectId) {
      url += `?project_id=${projectId}`;
    }

    const response = await fetch(url, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get sessions');
    }

    return response.json();
  }

  async deleteChatSession(sessionId, token, projectId = null) {
    let url = `${this.baseUrl}/chat/${sessionId}`;
    if (projectId) {
      url += `?project_id=${projectId}`;
    }

    const response = await fetch(url, {
      method: 'DELETE',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete session');
    }

    return response.json();
  }

  // Archive & Restore Methods
  
  /**
   * Archive a chat session (soft delete)
   */
  async archiveChatSession(sessionId, token, projectId = null) {
    let url = `${this.baseUrl}/chat/${sessionId}/archive`;
    if (projectId) {
      url += `?project_id=${projectId}`;
    }

    const response = await fetch(url, {
      method: 'PUT',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to archive session');
    }

    return response.json();
  }

  /**
   * Restore an archived chat session
   */
  async restoreChatSession(sessionId, token, projectId = null) {
    let url = `${this.baseUrl}/chat/${sessionId}/restore`;
    if (projectId) {
      url += `?project_id=${projectId}`;
    }

    const response = await fetch(url, {
      method: 'PUT',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to restore session');
    }

    return response.json();
  }

  /**
   * Get archived chat sessions
   */
  async getArchivedSessions(token, projectId = null, limit = 20) {
    let url = `${this.baseUrl}/chat/sessions/archived`;
    const params = new URLSearchParams();
    
    if (projectId) {
      params.append('project_id', projectId);
    }
    if (limit) {
      params.append('limit', limit.toString());
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get archived sessions');
    }

    return response.json();
  }

  // Ruling Configuration Methods
  
  /**
   * Get ruling configurations for a user
   */
  async getRulingConfigurations(token) {
    const response = await fetch(`${this.baseUrl}/ruling-configurations`, {
      method: 'GET',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get ruling configurations');
    }

    return response.json();
  }

  /**
   * Get ruling configurations for a specific project
   */
  async getProjectRulingConfigurations(projectId, token) {
    const response = await fetch(`${this.baseUrl}/projects/${projectId}/ruling-configurations`, {
      method: 'GET',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get project ruling configurations');
    }

    return response.json();
  }

  /**
   * Create a new ruling configuration
   */
  async createRulingConfiguration(configData, token, projectId = null) {
    const url = projectId 
      ? `${this.baseUrl}/projects/${projectId}/ruling-configurations`
      : `${this.baseUrl}/ruling-configurations`;

    const response = await fetch(url, {
      method: 'POST',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify(configData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create ruling configuration');
    }

    return response.json();
  }

  /**
   * Update an existing ruling configuration
   */
  async updateRulingConfiguration(configId, configData, token, projectId = null) {
    const url = projectId 
      ? `${this.baseUrl}/projects/${projectId}/ruling-configurations/${configId}`
      : `${this.baseUrl}/ruling-configurations/${configId}`;

    const response = await fetch(url, {
      method: 'PUT',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify(configData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update ruling configuration');
    }

    return response.json();
  }

  /**
   * Delete a ruling configuration
   */
  async deleteRulingConfiguration(configId, token, projectId = null) {
    const url = projectId 
      ? `${this.baseUrl}/projects/${projectId}/ruling-configurations/${configId}`
      : `${this.baseUrl}/ruling-configurations/${configId}`;

    const response = await fetch(url, {
      method: 'DELETE',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete ruling configuration');
    }

    return response.json();
  }

  /**
   * Get the effective ruling configuration for evaluation
   * This returns the configuration that should be used based on hierarchy:
   * 1. Project-specific configuration (if exists and active)
   * 2. User global configuration (if exists and active)
   * 3. System default configuration
   */
  async getEffectiveRulingConfiguration(token, projectId = null) {
    const url = projectId 
      ? `${this.baseUrl}/projects/${projectId}/ruling-configurations/effective`
      : `${this.baseUrl}/ruling-configurations/effective`;

    const response = await fetch(url, {
      method: 'GET',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get effective ruling configuration');
    }

    return response.json();
  }

  /**
   * Activate a ruling configuration (and deactivate others)
   */
  async activateRulingConfiguration(configId, token, projectId = null) {
    const url = projectId 
      ? `${this.baseUrl}/projects/${projectId}/ruling-configurations/${configId}/activate`
      : `${this.baseUrl}/ruling-configurations/${configId}/activate`;

    const response = await fetch(url, {
      method: 'POST',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to activate ruling configuration');
    }

    return response.json();
  }

  /**
   * Test a ruling configuration against sample data
   */
  async testRulingConfiguration(configData, testData, token) {
    const response = await fetch(`${this.baseUrl}/ruling-configurations/test`, {
      method: 'POST',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify({
        configuration: configData,
        test_data: testData
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to test ruling configuration');
    }

    return response.json();
  }

  /**
   * Clone a ruling configuration
   */
  async cloneRulingConfiguration(configId, newName, token, projectId = null) {
    const url = projectId 
      ? `${this.baseUrl}/projects/${projectId}/ruling-configurations/${configId}/clone`
      : `${this.baseUrl}/ruling-configurations/${configId}/clone`;

    const response = await fetch(url, {
      method: 'POST',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify({ name: newName })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to clone ruling configuration');
    }

    return response.json();
  }

  /**
   * Import ruling configuration from JSON
   */
  async importRulingConfiguration(configData, token, projectId = null) {
    const url = projectId 
      ? `${this.baseUrl}/projects/${projectId}/ruling-configurations/import`
      : `${this.baseUrl}/ruling-configurations/import`;

    const response = await fetch(url, {
      method: 'POST',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify(configData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to import ruling configuration');
    }

    return response.json();
  }

  /**
   * Export ruling configuration as JSON
   */
  async exportRulingConfiguration(configId, token, projectId = null) {
    const url = projectId 
      ? `${this.baseUrl}/projects/${projectId}/ruling-configurations/${configId}/export`
      : `${this.baseUrl}/ruling-configurations/${configId}/export`;

    const response = await fetch(url, {
      method: 'GET',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to export ruling configuration');
    }

    return response.json();
  }

  // ==================== REPORTING & APP APPROVAL ENDPOINTS ====================

  /**
   * Get analysis results by type (pass, fail, refer, other, error)
   */
  async getAnalysisResults(sessionId, projectId, token, status = null, limit = null) {
    let url = `${this.baseUrl}/analysis-results/${sessionId}?project_id=${projectId}`;
    
    if (status) {
      url += `&status=${status}`;
    }
    
    if (limit) {
      url += `&limit=${limit}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get analysis results');
    }

    return response.json();
  }

  /**
   * Approve or decline multiple apps
   */
  async approveDeclineApps(sessionId, projectId, action, appIds, reason, token) {
    const response = await fetch(`${this.baseUrl}/approve-decline-apps`, {
      method: 'POST',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify({
        session_id: sessionId,
        project_id: projectId,
        action: action,  // 'approve' or 'decline'
        app_ids: appIds,
        reason: reason
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to process app approvals');
    }

    return response.json();
  }

  /**
   * Get app actions history with filtering
   */
  async getAppActionsHistory(token, filters = {}) {
    let url = `${this.baseUrl}/app-actions-history?`;
    
    const params = new URLSearchParams();
    if (filters.sessionId) params.append('session_id', filters.sessionId);
    if (filters.projectId) params.append('project_id', filters.projectId);
    if (filters.action) params.append('action', filters.action);
    if (filters.appId) params.append('app_id', filters.appId);
    if (filters.userId) params.append('user_id', filters.userId);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    
    url += params.toString();

    const response = await fetch(url, {
      method: 'GET',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get actions history');
    }

    return response.json();
  }

  // Scorecard Management Methods
  async getAllScorecards(token, projectId = null, scorecardNumbers = null) {
    let url = projectId ? `${this.baseUrl}/projects/${projectId}/scorecards` : `${this.baseUrl}/scorecards`;
    
    const params = new URLSearchParams();
    if (scorecardNumbers) {
      params.append('scorecard_numbers', scorecardNumbers);
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get scorecards');
    }

    return response.json();
  }

  async getScorecard(scorecardId, token) {
    const response = await fetch(`${this.baseUrl}/scorecards/${scorecardId}`, {
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get scorecard');
    }

    return response.json();
  }

  async createScorecard(scorecardData, token, projectId = null) {
    const url = projectId ? `${this.baseUrl}/projects/${projectId}/scorecards` : `${this.baseUrl}/scorecards`;
    
    const response = await fetch(url, {
      method: 'POST',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify(scorecardData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create scorecard');
    }

    return response.json();
  }

  async updateScorecard(scorecardId, updates, token) {
    const response = await fetch(`${this.baseUrl}/scorecards/${scorecardId}`, {
      method: 'PUT',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update scorecard');
    }

    return response.json();
  }

  async deleteScorecard(scorecardId, token) {
    const response = await fetch(`${this.baseUrl}/scorecards/${scorecardId}`, {
      method: 'DELETE',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete scorecard');
    }

    return response.json();
  }

  // Session Import Methods
  async importSessionApps(sourceSessionId, targetSessionId, token, projectId = null, overwriteDuplicates = false) {
    const response = await fetch(`${this.baseUrl}/sessions/import`, {
      method: 'POST',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify({
        source_session_id: sourceSessionId,
        target_session_id: targetSessionId,
        project_id: projectId,
        overwrite_duplicates: overwriteDuplicates
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to import session apps');
    }

    return response.json();
  }

  // OpenAI Settings Methods
  async getUserOpenaiSettings(token) {
    const response = await fetch(`${this.baseUrl}/openai-settings`, {
      method: 'GET',
      ...this.getFetchOptions(this.getAuthHeaders(token))
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get OpenAI settings');
    }

    return response.json();
  }

  async saveUserOpenaiSettings(settings, token) {
    const response = await fetch(`${this.baseUrl}/openai-settings`, {
      method: 'POST',
      ...this.getFetchOptions(this.getAuthHeaders(token)),
      body: JSON.stringify(settings)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to save OpenAI settings');
    }

    return response.json();
  }
}

export default new ApiService();